import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Badge } from './ui';
import { formatPrice } from '../utils/currency';
import { StarDisplay } from './StarRating';
import TeacherByline from './TeacherByline';

/** A single sponsored-course card, reused by the Store's featured row and the
 * landing page's "Best Courses" slider — same visual, different action slot. */
export default function SponsoredCourseCard({ module: m, action, onView, className = '', centered = false }) {
  const { t } = useTranslation();
  return (
    <Card padded={false} className={`group h-full shrink-0 ${centered ? 'w-full rounded-3xl' : 'w-72'} flex flex-col overflow-hidden border-surface-200 hover:-translate-y-1 hover:border-primary-200 hover:shadow-card-hover transition duration-300 ${className}`}>
      <div
        className={`${centered ? 'h-44' : 'h-36'} bg-gray-100 bg-center bg-cover cursor-pointer overflow-hidden`}
        style={m.thumbnail ? { backgroundImage: `url(${m.thumbnail})` } : {}}
        onClick={onView}
      >
        {!m.thumbnail && (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-500 via-primary-600 to-accent-500">
            <svg className="w-9 h-9 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        )}
      </div>
      <div className={`${centered ? 'p-6' : 'p-4'} flex-1 flex flex-col ${centered ? 'items-center text-center' : ''}`}>
        <div className={`flex items-center gap-2 mb-2 ${centered ? 'justify-center flex-wrap' : 'justify-between w-full'}`}>
          <Badge tone="yellow">★ {t('Sponsored')}</Badge>
          <Badge tone={m.price && Number(m.price) > 0 ? 'blue' : 'green'}>
            {m.price && Number(m.price) > 0 ? formatPrice(m.price, m.currency) : t('Free')}
          </Badge>
        </div>
        <h3 className="font-display font-bold text-gray-900 mb-1 cursor-pointer hover:text-primary-600 transition" onClick={onView}>
          {m.name}
        </h3>
        <TeacherByline teachers={m.teachers} className={`mb-1.5 ${centered ? 'justify-center' : ''}`} />
        {m.average_rating != null && (
          <div className={`flex items-center gap-1.5 mb-1.5 ${centered ? 'justify-center' : ''}`}>
            <StarDisplay value={m.average_rating} size="w-3.5 h-3.5" />
            <span className="text-xs text-gray-500">{Number(m.average_rating).toFixed(1)} ({m.ratings_count})</span>
          </div>
        )}
        <p className={`text-sm text-gray-600 line-clamp-2 flex-1 ${centered ? 'text-center' : ''}`}>{m.description}</p>
        <div className={`mt-3 ${centered ? 'w-full flex justify-center' : ''}`}>{action}</div>
      </div>
    </Card>
  );
}
